import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, Calendar, Lightbulb, Monitor, GraduationCap, Heart, Target, FileText, Settings, Handshake, ChevronDown, ChevronUp } from 'lucide-react';
interface ServicePillar {
  id: number;
  title: string;
  icon: React.ReactNode;
  services: string[];
}
export const ServicePillars = () => {
  const [expandedPillar, setExpandedPillar] = useState<number | null>(null);
  const pillars: ServicePillar[] = [{
    id: 1,
    title: 'Community Building & Membership Ecosystems',
    icon: <Users size={32} data-id="element-584" data-mp="1fiyrpg0ynb5a" />,
    services: ['Curated professional communities (Inner Circle, Lounge)', 'High-trust ecosystem design', 'Private & paid membership structures', 'Relationship-led networking frameworks', 'Peer learning and accountability systems', 'Culture-shaper and creator communities']
  }, {
    id: 2,
    title: 'Salon-Style Networking & Experiential Events',
    icon: <Calendar size={32} data-id="element-585" data-mp="wmpbon6i0ain" />,
    services: ['Curated salon-style gatherings', 'Conversation-driven networking formats', 'Culture, tech, and creative leadership events', 'Community-led and invite-only experiences', 'High-intent connection design']
  }, {
    id: 3,
    title: 'Event Strategy, Production & Experience Design',
    icon: <Lightbulb size={32} data-id="element-586" data-mp="ttg4f1sumxqb" />,
    services: ['Event conceptualization and strategy', 'End-to-end experience design', 'Participant journey mapping (pre, during, post-event)', 'Onsite and virtual event enablement', 'Sponsor and partner integration', 'Community & technical partnerships for events']
  }, {
    id: 4,
    title: 'Digital Platforms, Websites & Technical Enablement',
    icon: <Monitor size={32} data-id="element-587" data-mp="17mxmsj9su4er" />,
    services: ['Website design and development', 'Event and community platforms', 'Registration and onboarding systems', 'Tool and workflow integration', 'Engagement and retention infrastructure', 'Technical operations for live experiences']
  }, {
    id: 5,
    title: 'Education, Training & Skill Retooling',
    icon: <GraduationCap size={32} data-id="element-588" data-mp="3d8ai64s8mjom" />,
    services: ['AI, web development, and future skills programs', 'Practical, tool-based learning', 'Career and employability retooling', 'Programs for professionals, creatives, and youth', 'Fast-execution learning pathways']
  }, {
    id: 6,
    title: 'Youth, Parent & Future Skills Advocacy',
    icon: <Heart size={32} data-id="element-589" data-mp="2zg4c5bistt2w" />,
    services: ['Youth skill-building initiatives', 'Parent education on future-ready skills', 'Early talent and relevance advocacy', 'Practical alternatives to outdated career paths', 'Long-term capacity building for young people']
  }, {
    id: 7,
    title: 'Consulting, Strategy & Advisory',
    icon: <Target size={32} data-id="element-590" data-mp="2lipke052p97r" />,
    services: ['Community and ecosystem strategy', 'Digital product and platform advisory', 'Experience and event strategy consulting', 'Growth and positioning advisory', 'Advisory for institutions, brands, and creators']
  }, {
    id: 8,
    title: 'Content, Media & Thought Leadership',
    icon: <FileText size={32} data-id="element-591" data-mp="ma7f9hud3i0m" />,
    services: ['Content strategy and planning', 'Educational content calendars', 'Thought leadership on culture, AI, and work', 'Event and community storytelling', 'Media assets for programs and partners']
  }, {
    id: 9,
    title: 'Tools, Systems & Experience Design',
    icon: <Settings size={32} data-id="element-592" data-mp="10zc0z90d84dh" />,
    services: ['Tool curation for creators and professionals', 'Systems that move ideas from concept to launch', 'Operational frameworks for scaling communities', 'UX microcopy, tooltips, and interaction design', 'Tech-enabled experience architecture']
  }, {
    id: 10,
    title: 'Social Impact, Non-Profit & Implementation Partnerships',
    icon: <Handshake size={32} data-id="element-593" data-mp="dblzfug9yh3j" />,
    services: ['Implementing partner for social impact projects', 'Program execution for NGOs and non-profits', 'Community and youth empowerment initiatives', 'Capacity-building and training delivery', 'Collaboration with institutions, foundations, and agencies', 'End-to-end project support (strategy → execution → impact)']
  }];
  const togglePillar = (id: number) => {
    setExpandedPillar(expandedPillar === id ? null : id);
  };
  return <section className="py-16 md:py-20 border-b-2 border-gray-300" style={{
    backgroundColor: '#FFFFFF'
  }} data-id="element-594" data-mp="1mbazvgnlzq4e">
      <div className="container mx-auto px-4" data-id="element-595" data-mp="214xlq1xxgtqo">
        <div className="max-w-6xl mx-auto" data-id="element-596" data-mp="1w7o0kyhkn48n">
          <div className="text-center mb-12" data-id="element-597" data-mp="1w6mg63eo15e9">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{
            color: '#0B1F3F'
          }} data-id="element-598" data-mp="2a76uam9myd1f">
              What We Build & Power
            </h2>
            <p className="text-lg md:text-xl max-w-3xl mx-auto" style={{
            color: '#666'
          }} data-id="element-599" data-mp="256riw08wzfm3">
              We don't just host events or build communities — we design
              ecosystems, power experiences, and move ideas forward with
              structure.
            </p>
          </div>

          {/* Service Pillars Grid - 5 per row on desktop, 2 rows */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 md:gap-6" data-id="element-600" data-mp="2oui86utai94p">
            {pillars.map(pillar => <motion.div key={pillar.id} className="relative group cursor-pointer" onClick={() => togglePillar(pillar.id)} whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} data-id="element-601" data-mp="37j2doegf3nv7">
                <div className="p-6 rounded-lg transition-all duration-300 h-full flex flex-col" style={{
              border: expandedPillar === pillar.id ? '2px solid #1AB9C8' : '2px dashed #E5E5E5',
              backgroundColor: expandedPillar === pillar.id ? '#F0FAFB' : '#FFFFFF'
            }} data-id="element-602" data-mp="33isa18g3l9ji">
                  {/* Icon */}
                  <div className="mb-4 p-3 rounded-full inline-flex self-start transition-colors" style={{
                backgroundColor: expandedPillar === pillar.id ? '#1AB9C8' : '#F0F0F0',
                color: expandedPillar === pillar.id ? '#FFFFFF' : '#0B1F3F'
              }} data-id="element-603" data-mp="2fvafx69d1zdd">
                    {pillar.icon}
                  </div>

                  {/* Title */}
                  <h3 className="text-base md:text-lg font-bold mb-2 flex-grow" style={{
                color: '#0B1F3F'
              }} data-id="element-604" data-mp="gffwgk1wz2bu">
                    {pillar.title}
                  </h3>

                  {/* Expand indicator */}
                  <div className="flex items-center justify-between mt-2" data-id="element-605" data-mp="7jzx5nie3uyv">
                    <span className="text-sm" style={{
                  color: '#666'
                }} data-id="element-606" data-mp="2okkgyw4c541">
                      {pillar.services.length} services
                    </span>
                    <motion.div animate={{
                  rotate: expandedPillar === pillar.id ? 180 : 0
                }} transition={{
                  duration: 0.3
                }} data-id="element-607" data-mp="vczxxgzlqfd9">
                      <ChevronDown size={20} style={{
                    color: '#1AB9C8'
                  }} data-id="element-608" data-mp="lcftk5gscuqt" />
                    </motion.div>
                  </div>
                </div>

                {/* Expanded Services Panel */}
                <AnimatePresence data-id="element-609" data-mp="365t0reb76wwn">
                  {expandedPillar === pillar.id && <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} exit={{
                opacity: 0,
                height: 0
              }} transition={{
                duration: 0.3
              }} className="absolute top-full left-0 right-0 mt-2 z-10 overflow-hidden" data-id="element-610" data-mp="2w144wk6atudf">
                      <div className="p-6 rounded-lg shadow-xl" style={{
                  backgroundColor: '#FFFFFF',
                  border: '2px solid #1AB9C8'
                }} data-id="element-611" data-mp="3eq11wvt2z0zc">
                        <h4 className="font-bold mb-3 text-sm" style={{
                    color: '#0B1F3F'
                  }} data-id="element-612" data-mp="3opruut1zt7xb">
                          Services Include:
                        </h4>
                        <ul className="space-y-2" data-id="element-613" data-mp="1uc0ezbt2lrtf">
                          {pillar.services.map((service, index) => <motion.li key={index} initial={{
                      opacity: 0,
                      x: -10
                    }} animate={{
                      opacity: 1,
                      x: 0
                    }} transition={{
                      delay: index * 0.05
                    }} className="flex items-start gap-2 text-sm" style={{
                      color: '#333'
                    }} data-id="element-614" data-mp="1zabqdqz0kadr" data-mpi={index}>
                              <span className="mt-1 flex-shrink-0" style={{
                        color: '#1AB9C8'
                      }} data-id="element-615" data-mp="9ur97uc2d0cm">
                                •
                              </span>
                              <span data-id="element-616" data-mp="3r8wm6rty03u1">{service}</span>
                            </motion.li>)}
                        </ul>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </motion.div>)}
          </div>

          {/* Bottom CTA */}
          <div className="text-center mt-12" data-id="element-617" data-mp="16kif0ppt29xq">
            <p className="text-lg mb-6" style={{
            color: '#666'
          }} data-id="element-618" data-mp="12hu7qmgqpb2v">
              Interested in partnering with us or learning more about our
              services?
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-619" data-mp="1f0pquf5v87vz">
              <a href="/partner-with-us" data-id="element-620" data-mp="102xe1urpdxlh">
                <button className="px-8 py-3 rounded-md font-bold text-white transition-all hover:opacity-90" style={{
                backgroundColor: '#0B1F3F'
              }} data-id="element-621" data-mp="103yrg5w6yw6d">
                  Partner with Us
                </button>
              </a>
              <a href="/contact" data-id="element-622" data-mp="1xqpd6uabn8vp">
                <button className="px-8 py-3 rounded-md font-bold transition-all hover:opacity-90" style={{
                backgroundColor: '#FFFFFF',
                color: '#0B1F3F',
                border: '2px solid #0B1F3F'
              }} data-id="element-623" data-mp="1xout1i6vjkso">
                  Get in Touch
                </button>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>;
};